// DispatchDeck — shared `_counters` allocation retry (TASK-4825)
//
// ONE loop, used by every client-side sequential-number allocator in the app.
// Extracted from billing.js (TASK-4823), which is where it was written, measured
// and proven. Nothing about the algorithm changed in the move; the constants,
// the ordering of the deadline checks and the backoff curve are byte-identical
// to the version that shipped in v1.15.1892 and is asserted in
// tests/ticket-number-allocator.spec.js.
//
// ─────────────────────────────────────────────────────────────────────────────
// 🔴 WHY THIS EXISTS: THE RETRY IS NOT OPTIONAL
// ─────────────────────────────────────────────────────────────────────────────
// Every `_counters` doc in this app is guarded by ONE monotonic firestore.rules
// clause: `request.resource.data.value > resource.data.value`. That clause turns
// a LOST transaction race into **permission-denied** instead of the ABORTED
// Firestore would re-run on its own, and the Web SDK treats permission-denied as
// FATAL and will not retry. So every client allocator built on that counter has
// the same defect: the number is never DUPLICATED, it is simply never ISSUED.
//
// Measured against real rules on throwaway tenants:
//   ticketNo  N=5 → 1 ok / 4 permission-denied     N=50 → 2 ok / 48 denied
//   jobNo     N=5 → 1 ok / 4 permission-denied
//   slips     N=5 → 1 ok / 4 permission-denied     (TASK-4825, see report)
//
// ─────────────────────────────────────────────────────────────────────────────
// 🎓 WHY ONE MODULE AND NOT FOUR COPIES — and why this is NOT Pitfall #1
// ─────────────────────────────────────────────────────────────────────────────
// FOUR client allocators share this exact shape:
//   • getNextTicketNumber     views/billing.js
//   • allocateInvoiceNumber   views/billing.js
//   • getNextSlipNumber       utils/slipHelpers.js
//   • allocateJobNumber       views/dispatch.js
// Three separate lanes wrote three separate copies of this loop on three
// separate nights, and they had ALREADY DRIFTED before they were ever collapsed:
// dispatch.js shipped a 24-attempt budget with no wall clock while billing.js
// shipped 64 + 30s, and the 24 was measured leaving 17 of 50 callers with no
// number at all. A fifth copy is how that happens again. So: one module.
//
// 🔴 Pitfall #1 says never cross-import a module with a `?v=` buster, because two
// importers on different busters get two MODULE INSTANCES. That hazard is about
// module-level STATE (it is why trailguide.js is reached through
// `window.Trailguide`). This module holds NO mutable state — three frozen
// constants and one pure-control-flow async function. Two instances of it would
// behave identically, so the hazard does not apply, and the fail-open risk of the
// `window.*` alternative does. A `window.CounterRetry` handoff that had not been
// published yet at call time would leave allocation running WITHOUT the retry and
// throw nothing — reintroducing exactly the defect this file exists to fix, in
// the silent-failure family. An import cannot fail that way.
// (Two `utils/` modules — imageProcessor.js and slipHelpers.js — are already
// cross-imported by these same views at matched busters; this is the house
// pattern, not a new one. And in the bundle prod actually serves, esbuild's
// dd-resolver strips `?v=` during resolution, so all importers dedupe to ONE
// instance regardless — see scripts/build-frontend.cjs.)
//
// 🔴 IMPORTERS, KEEP THE BUSTER IN LOCKSTEP. When this file changes, bump `?v=`
// in ALL of: views/billing.js, views/dispatch.js, utils/slipHelpers.js.
//
// ─────────────────────────────────────────────────────────────────────────────
// ⚠️ THE SERVER TWINS DELIBERATELY DO NOT USE THIS, AND MUST NOT
// ─────────────────────────────────────────────────────────────────────────────
// functions/src/services/ticketGateway.js (getNextTicketNumberServer and the
// invoice allocator inside issueInvoiceNumber) run on the Admin SDK, which
// BYPASSES firestore.rules entirely. A lost race there never becomes
// permission-denied — it is a plain ABORTED, which the Admin SDK's own
// transaction machinery already re-runs against the committed value. Wrapping
// them in this loop would add a second retry on top of a working one and make a
// genuine authz/quota failure take up to 30s to surface. Left alone on purpose.
//
// ─────────────────────────────────────────────────────────────────────────────
// A retry is SAFE here specifically BECAUSE the counter is monotonic: each
// attempt re-reads inside a fresh transaction and writes current+1, so it can
// never re-issue a number, only take the next free one.
//
// ⚠️ `permission-denied` is retryable here, which means a GENUINE authz denial
// (a member who may not write counters at all) does not fail fast — it retries
// until one of the two bounds below trips. That is deliberate: from inside the
// loop a lost race and an honest denial are indistinguishable. Bounded, so the
// honest denial still surfaces, just late, and it is NEVER swallowed —
// `lastErr` is re-thrown.
//
// ⚠️ TWO BOUNDS, NOT ONE, AND THE SECOND ONE IS THE IMPORTANT ONE.
//
// v1 of this fix had only an attempt count, and its comment claimed a worst case
// of "~24 rounds × ≤280ms ≈ 4s". COD's Code Reviewer called that out: an attempt
// budget priced in BACKOFF alone ignores the transaction round-trip on every
// attempt. Measured, N=50 contending allocations on `_e2e_ticketno`, slowest
// caller: **66,243ms and 72,833ms** across two runs. Not 4s, not 14s — over a
// MINUTE. A driver at clock-out would have concluded the app was dead.
//
// THE BOUND IS A WALL CLOCK, because an attempt count cannot see the round-trip
// and therefore cannot bound latency. Whichever bound trips first ends the loop.
//
// ⚠️ BE PRECISE ABOUT WHAT THE 30s ACTUALLY BOUNDS — it is a RETRY-START budget,
// not a hard ceiling on the caller's total wall time. No NEW attempt begins once
// the deadline has passed, but the attempt already in flight is never abandoned
// (deliberately: a bare timeout race would leave a transaction running and is
// how you get a number issued to nobody). So the honest total a caller can spend
// is: any max-probe up front, which runs BEFORE this helper is called and can add
// its own retry, PLUS up to 30s of retry-starts, PLUS however long the final
// in-flight transaction takes.
//
// 🔴 WHAT WAS TRIED AND DID NOT WORK, recorded so nobody re-tries it: the narrow
// `fixed + jitter` window below was replaced with textbook FULL JITTER
// (`random(0, min(cap, base × 2^attempt))`, cap 1500ms) on the theory that the
// contenders were re-colliding in lockstep. MEASURED, it was strictly WORSE:
//   curve            N=5     N=10    N=25
//   fixed+jitter     2391ms  4189ms  17461ms, 0 failed
//   full jitter      4173ms  6877ms  20838ms, 4 FAILED
// Sleeping longer to de-synchronise costs more than the collisions it avoids,
// because a loser discovers it lost only by spending a whole transaction
// round-trip. The aggressive narrow window retries sooner and wins. Reverted.
//
// 🔴 AND THE REAL CEILING IS NOT THIS LOOP AT ALL: Firestore sustains roughly
// ONE WRITE PER SECOND to a single document, and every allocation is a write to
// the same `_counters` doc. 50 allocations on one counter therefore cannot
// physically finish much faster than ~50s no matter what the retry curve does —
// measured slowest caller at N=50 was 57,972 / 66,243 / 72,833ms across three
// runs. So "zero failures at N=50" and "no caller waits more than ~30s" are NOT
// both achievable, and the choice here is deliberate: BOUND THE WAIT and let the
// physically-impossible case take the degraded path. Callers must therefore have
// a degraded path (ticketNoPending / jobNumberPending) or must surface the
// failure honestly — see each caller.
//
// WHY 30s AND NOT 5s: the caller this protects is clock-out
// (clock.js findOrCreateTicket), which does NOT block on it — it degrades to a
// ticket stamped `ticketNoPending`, a flagged, queryable row that renders a
// visible "no #" badge. So the honest question is "how long is it worth waiting
// before taking the degraded path", not "how long may a driver be frozen". 30s
// clears N=25 (17.5s measured) with margin — already far past what a real yard
// produces — and refuses to hang for over a minute.
//
// The measured latencies are ASSERTED in tests/ticket-number-allocator.spec.js
// and tests/slip-number-allocator.spec.js, not merely described here — a latency
// claim that lives only in a comment is exactly what went wrong the first time.
export const COUNTER_MAX_ATTEMPTS = 64;
export const COUNTER_MAX_WALL_MS = 30000;
export const COUNTER_RETRYABLE = new Set([
  'permission-denied', 'aborted', 'failed-precondition', 'unavailable', 'deadline-exceeded',
]);

/**
 * Run one counter allocation, retrying the monotonic-rule race.
 *
 * @param {string} label   Only used in the exhaustion message ("job number").
 * @param {() => Promise<number>} runTxn
 *        MUST be a thunk that runs ONE fresh runTransaction() per call. Anything
 *        expensive that does not change between attempts (a max-probe query)
 *        belongs OUTSIDE it, in the caller: by the time we are retrying the
 *        counter has already moved PAST any max the probe could have seen, so
 *        re-probing every round costs a query per round and changes nothing.
 * @returns {Promise<number>} whatever runTxn resolved to.
 */
export async function allocateCounterWithRetry(label, runTxn) {
  // ⏱️ performance.now(), NOT Date.now() — COD Code Reviewer. Date.now() follows
  // the system clock, so an NTP step or a user changing their clock mid-loop can
  // make this budget expire instantly or effectively never. performance.now() is
  // monotonic and immune to both. It is a browser clock and every caller of this
  // module is browser-only.
  const deadline = performance.now() + COUNTER_MAX_WALL_MS;
  let lastErr = null;
  for (let attempt = 0; attempt < COUNTER_MAX_ATTEMPTS; attempt++) {
    // 🔴 THE DEADLINE IS CHECKED HERE, BEFORE STARTING A TRANSACTION — not only
    // before sleeping. COD's Code Reviewer caught exactly this: v2 checked only
    // before the sleep, clamped the sleep to land ON the deadline, and then
    // started another transaction unconditionally. The bound was described but
    // not enforced, and the measurement agreed — slowest caller came in at
    // 32,641ms and 32,411ms against a 30,000ms budget. Shipping a bound the code
    // does not actually keep is the same class of defect as the comment this
    // task exists to fix.
    // `attempt > 0` so the FIRST attempt always runs: a caller must never be
    // refused without the allocator having tried at all.
    if (attempt > 0 && performance.now() >= deadline) break;
    try {
      return await runTxn();
    } catch (e) {
      lastErr = e;
      // A plain Error thrown by the transaction body (no `.code`) is a decision,
      // not a race — "nothing safe to return", or the invoice self-heal ceiling.
      // Those must propagate on the FIRST attempt, never burn the whole budget.
      if (!COUNTER_RETRYABLE.has(e?.code)) throw e;
      // Checked AFTER the throw-through above, so a fatal error is never reported
      // as a timeout — and BEFORE the sleep, so we never wait out a budget that is
      // already spent.
      if (performance.now() >= deadline) break;
      // Narrow, aggressive, jittered — capped at 160ms + up to 120ms of jitter.
      // Measured better than full jitter at every contention level (see above).
      const backoff = Math.round(Math.min(2 ** attempt * 20, 160) + Math.random() * 120);
      // Never sleep PAST the deadline, or the final wait overshoots the very
      // bound this block exists to enforce.
      await new Promise((r) => setTimeout(r, Math.max(0, Math.min(backoff, deadline - performance.now()))));
    }
  }
  throw lastErr || new Error(`Could not allocate a ${label} after ${COUNTER_MAX_ATTEMPTS} attempts`);
}

// Belt-and-suspenders, and the handle the E2E suites assert on: a test can prove
// all four allocators share ONE loop by comparing identity, and can read the
// constants back rather than hard-coding a copy of them that would silently drift.
// NOT the transport — importers `import` this module; the global is diagnostics.
if (typeof window !== 'undefined') {
  window.CounterRetry = {
    allocateCounterWithRetry,
    COUNTER_MAX_ATTEMPTS,
    COUNTER_MAX_WALL_MS,
    COUNTER_RETRYABLE,
  };
}
